import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Edit2, ChevronRight, Lock } from 'lucide-react'
import { useCollections } from '../hooks/useCollections'
import { usePlacesMap } from '../hooks/usePlacesMap'
import { useRankedPeople, type RankedPersonItem } from '../hooks/useRankedPeople'
import { collectionsApi } from '../services/collectionsApi'
import CreateCollectionModal from '../components/collections/CreateCollectionModal'
import EditCollectionModal from '../components/collections/EditCollectionModal'
import PlacesMap, {
    PlaceMapItem,
    PlacesMapViewMode,
} from '../components/places/PlacesMap'
import PlacesViewModeControl from '../components/places/PlacesViewModeControl'
import RankedRail, { RANKED_MAX_COUNT } from '../components/people/RankedRail'
import LocationDetailModal, {
    LocationDetailValue,
} from '../components/checkin/LocationDetailModal'
import PersonDetailModal from '../components/checkin/PersonDetailModal'
import type { Collection } from '../types/collections'
import { useNotify } from '../components/notice/useNotify'

/**
 * Collections index page. Three stacked sections, top-to-bottom:
 *
 *   1. A user-wide places heatmap/cluster map — same component + same
 *      `/api/places/map` fetch as PlacesCollectionPage, just view-only here
 *      (clicks open LocationDetailModal, no edit/picker).
 *   2. Recent (last 30 days) + Closest (lifetime) ranked people rails —
 *      same component + same `/api/people/ranked` fetch as
 *      PeopleCollectionPage. Cards are view-only here; clicks open
 *      PersonDetailModal, no edit/delete buttons.
 *   3. The actual list of *user* collections (non-system). System
 *      collections (People, Places) are intentionally excluded from this
 *      grid since their content already surfaces in sections 1 + 2 above.
 *
 * Each preview section hides itself when its data is empty, so a brand-new
 * user with no checkins still gets a clean "create your first collection"
 * page without empty map / rail shells.
 */

const CollectionsPage: React.FC = () => {
    const navigate = useNavigate()
    const { collections, loading, error, refetch } = useCollections()
    const notify = useNotify()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editingCollection, setEditingCollection] = useState<Collection | null>(null)
    const [deletingId, setDeletingId] = useState<number | null>(null)

    // Preview-section data. Both fetch on mount; failures degrade gracefully
    // to a hidden section (see has* checks below).
    const { places, unnamedPins, loading: placesLoading } = usePlacesMap()
    const { ranked } = useRankedPeople(RANKED_MAX_COUNT)

    const [viewMode, setViewMode] = useState<PlacesMapViewMode>('heatmap')
    const [selectedPlace, setSelectedPlace] = useState<PlaceMapItem | null>(null)
    const [detailCoords, setDetailCoords] = useState<{ lat: number; lng: number } | null>(null)
    const [viewingPerson, setViewingPerson] = useState<RankedPersonItem | null>(null)

    // Custom (non-system) collections only — system collections render via
    // the preview sections above instead.
    const userCollections = useMemo(
        () => collections.filter((c) => !c.is_system),
        [collections],
    )

    // System-collection IDs so the section headers can deep-link into the
    // dedicated full-surface pages (PlacesCollectionPage / PeopleCollectionPage).
    const placesCollection = useMemo(
        () => collections.find((c) => c.system_kind === 'places'),
        [collections],
    )
    const peopleCollection = useMemo(
        () => collections.find((c) => c.system_kind === 'people'),
        [collections],
    )

    const mapPlaces = useMemo(
        () => places.filter((p) => p.lat != null && p.lng != null),
        [places],
    )
    const hasMapData = !placesLoading && (mapPlaces.length > 0 || unnamedPins.length > 0)
    const hasRanked = ranked != null && (ranked.recent.length > 0 || ranked.lifetime.length > 0)

    const detailValue = useMemo<LocationDetailValue | null>(() => {
        if (selectedPlace) {
            return {
                collectionItemId: selectedPlace.id,
                lat: selectedPlace.lat ?? undefined,
                lng: selectedPlace.lng ?? undefined,
                name: selectedPlace.name,
                address: selectedPlace.address,
                googlePlaceId: selectedPlace.google_place_id,
                categoryName: selectedPlace.category_name,
                categorySlug: selectedPlace.category_slug,
            }
        }
        if (detailCoords) {
            return { lat: detailCoords.lat, lng: detailCoords.lng }
        }
        return null
    }, [selectedPlace, detailCoords])

    const handleDelete = async (collection: Collection) => {
        if (!confirm(`Are you sure you want to delete "${collection.name}"? This will also delete all items in this collection.`)) {
            return
        }

        setDeletingId(collection.id)
        try {
            await collectionsApi.delete(collection.id)
            await refetch()
        } catch (err) {
            console.error('Error deleting collection:', err)
            notify.error('Failed to delete collection')
        } finally {
            setDeletingId(null)
        }
    }

    const handleViewCollection = (collection: Collection) => {
        navigate(`/collections/${collection.id}`)
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading collections...</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-rose-500">{error}</div>
            </div>
        )
    }

    return (
        <div className="max-w-6xl mx-auto px-4 pb-8 sm:pt-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Collections</h1>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors cursor-pointer"
                >
                    <Plus className="w-5 h-5" />
                    Create
                </button>
            </div>

            {/* Places preview map — same component + fetch as the dedicated
                Places page. View-only here: clicks open the read-only detail
                modal, no editor. Header "View all" deep-links into the full
                PlacesCollectionPage. */}
            {hasMapData && (
                <div className="mb-6">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-baseline gap-3 min-w-0">
                            <h2 className="text-lg font-semibold text-gray-800">Places</h2>
                            {placesCollection && (
                                <button
                                    onClick={() => navigate(`/collections/${placesCollection.id}`)}
                                    className="text-xs text-amber-800 hover:text-amber-900 inline-flex items-center gap-0.5 cursor-pointer"
                                >
                                    View all
                                    <ChevronRight className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                        <PlacesViewModeControl value={viewMode} onChange={setViewMode} />
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <PlacesMap
                            places={mapPlaces}
                            unnamedPins={unnamedPins}
                            viewMode={viewMode}
                            loading={placesLoading}
                            heightClass="h-[320px]"
                            onPlaceClick={(p) => {
                                setDetailCoords(null)
                                setSelectedPlace(p)
                            }}
                            onPinClick={(c) => {
                                setSelectedPlace(null)
                                setDetailCoords(c)
                            }}
                        />
                    </div>
                </div>
            )}

            {/* People preview rails — Recent + Closest. Same fetch as the
                People page; view-only here so the cards only render an
                avatar/name and open the read-only PersonDetailModal on tap.
                Header "View all" deep-links into the full PeopleCollectionPage. */}
            {hasRanked && (
                <div className="mb-6">
                    <div className="flex items-baseline gap-3 mb-2">
                        <h2 className="text-lg font-semibold text-gray-800">People</h2>
                        {peopleCollection && (
                            <button
                                onClick={() => navigate(`/collections/${peopleCollection.id}`)}
                                className="text-xs text-amber-800 hover:text-amber-900 inline-flex items-center gap-0.5 cursor-pointer"
                            >
                                View all
                                <ChevronRight className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        <RankedRail
                            title="Recent"
                            subtitle={`Last ${ranked!.recent_days} days`}
                            people={ranked!.recent}
                            onView={(p) => setViewingPerson(p)}
                        />
                        <RankedRail
                            title="Closest"
                            subtitle="All time"
                            people={ranked!.lifetime}
                            onView={(p) => setViewingPerson(p)}
                        />
                    </div>
                </div>
            )}

            {userCollections.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No collections yet. Create your first collection to get started.</p>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors cursor-pointer"
                    >
                        <Plus className="w-5 h-5" />
                        Create
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {userCollections.map((collection) => (
                        <div
                            key={collection.id}
                            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-xl font-semibold text-gray-900">{collection.name}</h3>
                                    {collection.is_system && (
                                        <span
                                            className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                                            title="Fixed structure. Items are user-managed and may also be added automatically by Honeyguide."
                                        >
                                            <Lock className="w-3 h-3" />
                                            Fixed structure
                                        </span>
                                    )}
                                </div>
                                {!collection.is_system && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setEditingCollection(collection)}
                                            className="p-1 text-gray-600 hover:text-amber-900 transition-colors cursor-pointer"
                                            title="Edit collection"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(collection)}
                                            disabled={deletingId === collection.id}
                                            className="p-1 text-gray-600 hover:text-rose-600 transition-colors disabled:opacity-50 cursor-pointer"
                                            title="Delete collection"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {collection.description && (
                                <p className="text-gray-600 mb-4 line-clamp-2">{collection.description}</p>
                            )}

                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-500">
                                    {Object.keys(collection.config.fields).length} fields
                                </div>
                                <button
                                    onClick={() => handleViewCollection(collection)}
                                    className="flex items-center gap-1 text-amber-800 hover:text-amber-900 transition-colors cursor-pointer"
                                >
                                    View items
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isCreateModalOpen && (
                <CreateCollectionModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={() => {
                        setIsCreateModalOpen(false)
                        refetch()
                    }}
                />
            )}

            {editingCollection && (
                <EditCollectionModal
                    collection={editingCollection}
                    onClose={() => setEditingCollection(null)}
                    onSuccess={() => {
                        setEditingCollection(null)
                        refetch()
                    }}
                />
            )}

            {/* View-only location detail for map clicks (place pin → full
                info; unnamed pin → bare coords). No edit affordance — the
                Places collection page is the place for editing. */}
            <LocationDetailModal
                open={detailValue != null}
                value={detailValue}
                onClose={() => {
                    setSelectedPlace(null)
                    setDetailCoords(null)
                }}
            />

            {/* View-only person detail for rail clicks. No edit affordance
                here — the People collection page is the place for editing. */}
            <PersonDetailModal
                open={viewingPerson !== null}
                value={
                    viewingPerson
                        ? {
                            collectionItemId: viewingPerson.collection_item_id,
                            data: viewingPerson.data,
                        }
                        : null
                }
                onClose={() => setViewingPerson(null)}
            />
        </div>
    )
}

export default CollectionsPage
